// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import React, { useEffect } from "react";

import { GridWidths } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Typography } from "@insite/mobius/index";
import Link from "@store/MobiusOverrides/Link/Link";
import useProductContextsBySkus from "@store/Framework/Common/Hooks/useProductContextsBySku";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { ReportOrderFrequencyItemModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import useOrderFrequencyState from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useOrderFrequencyState";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import AddToCartAndWishList from "@store/Library/Components/Reporting/AddToCartAndWishList/AddtoCartAndWishList";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";
import useReportingProductLinkFormatting from "@store/Library/Widgets/Reports/useReportingLinkFormatting";
import { historyFilterPropsAlt } from "@store/Themes/common/props/history";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");

export const searchFormStyles = {
    headingText: { variant: "h3" },
    gridContainer: historyFilterPropsAlt.gridContainer,
    gridItem: historyFilterPropsAlt.gridItem,
    gridItemDate: { width: [12, 12, 12, 6, 6] as GridWidths[] },
};

const styles = searchFormStyles;

const reportFrequencyTableHeaders = ["Times Ordered", "Item Number", "Name", "Last Ordered"];
const reportFrequencyDataKeys: (keyof ReportOrderFrequencyItemModel)[] = [
    "frequencyCount",
    "itemNumber",
    "displayName",
    "lastOrdered",
];
export const OrderFrequencySearchForm: React.FunctionComponent = () => {
    const { updateData, isLoading, isError, hasSubmitted, setHasSubmitted } = useReportApi("orderFrequency");
    const { productContextsBySku, getProductContextsBySkus } = useProductContextsBySkus();
    const onClear = useClear();
    const [reportData] = useOrderFrequencyState();
    const [elements, setElements] = React.useState<JSX.Element[]>([]);
    const reportParameters = useSelector(reportParametersSelector);
    const [productLinks, setProductLinks] = React.useState<JSX.Element[]>([]);
    const handleDataUpdate = async () => {
        await updateData({ ...reportParameters });
    };

    const { getProductLinkFieldByName, getProductLinkBySku } = useReportingProductLinkFormatting(
        "displayName",
        "itemNumber",
        reportData?.items,
        productContextsBySku,
    );

    useEffect(() => {
        if (reportData) {
            getProductContextsBySkus(reportData.items.map(item => item.itemNumber.trim()));
        }
    }, [reportData?.items.length]);
    useEffect(() => {
        if (reportData) {
            const skus = reportData.items.map(item => item.itemNumber.trim());
            const elements = skus.map(sku => {
                return <AddToCartAndWishList productContextsBySku={productContextsBySku} key={sku} sku={sku} />;
            });
            setElements(elements);
        }
    }, [productContextsBySku, reportData?.items.length]);
    const reportFrequencyData =
        reportData?.items.map((data, index) => ({ ...data, extraElement: elements?.[index] })) || [];
    const formatDictionary: FormatDictionary = {
        displayName: (value: string) => getProductLinkFieldByName(value),
        itemNumber: (value: string) => getProductLinkBySku(value),
    };

    const reportFrequencyTableData: DataTableData<ReportOrderFrequencyItemModel> = {
        title: translate("Order Frequency Report"),
        headers: reportFrequencyTableHeaders,
        data: reportFrequencyData,
        dataKeys: reportFrequencyDataKeys,
        dataSummaryTitle: translate("Total Items"),
        uniqueKey: "itemNumber",
        formatDictionary,
    };
    const handleClear = () => {
        onClear();
        setHasSubmitted(false);
    };
    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem doubleSized={true}>
                    <DatePicker />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <LocationPicker isError={isError} />
                </ReportingFilterGridItem>
                <ApiButtons onClear={handleClear} isLoading={isLoading} updateData={handleDataUpdate} />
            </AccordionLayout>
            {reportFrequencyData.length > 0 && <ReportDataTable dataTableData={reportFrequencyTableData} />}
            {reportFrequencyData.length === 0 && hasSubmitted && !isLoading && (
                <Typography variant="h3">{translate("No results found")}</Typography>
            )}
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: OrderFrequencySearchForm,
    definition: {
        group: "Reporting",
        displayName: "Order Frequency Report",
    },
};

export default widgetModule;
